// v3.0.0-rc.8 — CartDrawer : panier unifié (livraisons en cours + réservations)
// Drawer glissant depuis la droite, ne quitte pas le chat.
// Data : /api/v3/delivery/my-client-orders + /api/v3/me/appointments (JWT required).
// Non-auth → CTA "Connexion". Empty state → CTA "Voir la boutique".

import { useEffect, useState, useCallback } from "react";
import { readCart, setQty, clearCart, cartCount as _localCount, subscribe as subLocalCart } from "@/lib/localCartStore";
import FamilyBasketShareCard from "@/components/FamilyBasketShareCard";
import MobileMoneyCheckout from "@/components/payments/MobileMoneyCheckout";
import ClaimAddressWizard from "@/components/ClaimAddressWizard";
import OrderTrackingLive from "@/components/OrderTrackingLive";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const STATUS_LABEL = {
  pending_acceptance: { txt: "En attente d'un livreur", color: "#F59E0B" },
  accepted: { txt: "Accepté par un livreur", color: "#6366F1" },
  picking_up: { txt: "Livreur en route (retrait)", color: "#6366F1" },
  picked_up: { txt: "Colis récupéré", color: "#10B981" },
  delivering: { txt: "En livraison", color: "#10B981" },
  delivered: { txt: "Livré ✓", color: "#0F172A" },
  failed: { txt: "Échec", color: "#EF4444" },
  cancelled: { txt: "Annulé", color: "#94A3B8" },
  mock_pending: { txt: "En attente confirmation", color: "#F59E0B" },
};

function fmtFcfa(n) {
  if (!Number.isFinite(Number(n))) return "—";
  return Number(n).toLocaleString("fr-FR") + " FCFA";
}

function fmtDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short" }) +
      " à " + d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
  } catch (_) { return ""; }
}

export default function CartDrawer({ open, onClose, onGoToAccount }) {
  const [tab, setTab] = useState("all"); // all | items | orders | bookings
  const [orders, setOrders] = useState([]);
  const [dispatchOrders, setDispatchOrders] = useState([]); // v3.0.0-rc.8k — orders chaîne sacrée
  const [bookings, setBookings] = useState([]);
  const [localItems, setLocalItems] = useState(() => readCart());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [authNeeded, setAuthNeeded] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(null); setAuthNeeded(false);
    const token = localStorage.getItem("civ_user_token") || "";
    if (!token) { setAuthNeeded(true); setLoading(false); return; }
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [oRes, bRes, dRes] = await Promise.all([
        fetch(`${API}/v3/delivery/my-client-orders?active=true`, { headers }),
        fetch(`${API}/v3/me/appointments`, { headers }),
        // v3.0.0-rc.8k — Cart Tracking Sync : orders chaîne sacrée (dispatch_orders)
        fetch(`${API}/v3/dispatch/orders/mine?active=true&limit=20`, { headers }),
      ]);
      if (oRes.status === 401 || bRes.status === 401 || dRes.status === 401) {
        setAuthNeeded(true); setLoading(false); return;
      }
      const oData = oRes.ok ? await oRes.json() : { orders: [] };
      const bData = bRes.ok ? await bRes.json() : { appointments: [] };
      const dData = dRes.ok ? await dRes.json() : { orders: [] };
      setOrders(oData.orders || []);
      setBookings(bData.appointments || []);
      setDispatchOrders(dData.orders || []);
    } catch (e) {
      setError("Impossible de charger votre panier pour le moment.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  // v3.0.0-rc.8e — Souscription aux mutations du panier local (custom event).
  useEffect(() => {
    setLocalItems(readCart());
    const off = subLocalCart((items) => setLocalItems(items));
    return () => { try { off && off(); } catch (_) { /* noop */ } };
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // v3.0.0-rc.8k — Order Recovery : rouvre une commande dans le chat.
  // Émet le même event que le cart checkout success avec un seul order → Chat.jsx
  // injecte la carte de suivi et le récap dans la conversation active.
  const reopenInChat = useCallback((order) => {
    try {
      window.dispatchEvent(new CustomEvent("aya:cart-checkout-success", {
        detail: {
          orders: [{
            merchant_id: order.merchant_id,
            merchant_name: order.merchant_name || "Commerçant",
            order_id: order.order_id,
            order_ref: order.order_id,
            pin: null, // PIN déjà remis au client — non affiché en recovery
            amount_fcfa: order.amount_fcfa,
            items_count: 1,
            items: [{ name: order.item_summary || "commande", kind: "product", qty: 1, price_fcfa: order.amount_fcfa }],
            item_summary: order.item_summary,
            dispatch_started: true,
            verdict: "APPROVED",
            status: order.status || "confirmed",
          }],
          errors: [],
          delivery_address_civ: order.drop?.code || null,
          total_fcfa: order.amount_fcfa,
          parent_entry_id: null,
          new_balance_fcfa: null,
          recovery: true,
        },
      }));
    } catch (_) { /* noop */ }
    onClose?.();
  }, [onClose]);

  if (!open) return null;

  const showItems = tab === "all" || tab === "items";
  const showOrders = tab === "all" || tab === "orders";
  const showBookings = tab === "all" || tab === "bookings";
  const itemsCount = localItems.reduce((s, x) => s + (x.qty || 0), 0);
  const itemsTotal = localItems.reduce((s, x) => s + (x.qty || 0) * (x.price_fcfa || 0), 0);
  const totalItems = orders.length + bookings.length + itemsCount + dispatchOrders.length;

  return (
    <div
      data-testid="cart-drawer-root"
      style={{
        position: "fixed", inset: 0, zIndex: 200,
        display: "flex", justifyContent: "flex-end",
      }}
    >
      {/* Backdrop */}
      <div
        data-testid="cart-drawer-backdrop"
        onClick={onClose}
        style={{
          position: "absolute", inset: 0,
          background: "rgba(15,23,42,0.55)",
          backdropFilter: "blur(2px)",
          animation: "cartFadeIn 180ms ease-out",
        }}
      />
      {/* Drawer */}
      <aside
        data-testid="cart-drawer"
        role="dialog"
        aria-label="Panier"
        style={{
          position: "relative",
          width: "min(420px, 100vw)",
          maxWidth: "100vw",
          background: "var(--civ-bg, #ffffff)",
          color: "var(--civ-text, #0f172a)",
          boxShadow: "-16px 0 40px rgba(0,0,0,0.25)",
          display: "flex", flexDirection: "column",
          animation: "cartSlideIn 220ms cubic-bezier(0.16, 1, 0.3, 1)",
        }}
      >
        {/* Header */}
        <header style={{
          padding: "14px 16px",
          borderBottom: "1px solid var(--civ-border, rgba(0,0,0,0.08))",
          display: "flex", alignItems: "center", gap: 10,
        }}>
          <div style={{
            width: 34, height: 34, borderRadius: 12,
            background: "linear-gradient(135deg, #FF7E1B, #F59E0B)",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 18, flexShrink: 0,
          }}>🛒</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 800, fontSize: 16, lineHeight: 1.1 }}>Mon panier</div>
            <div style={{ fontSize: 12, opacity: 0.7 }}>
              {totalItems} {totalItems > 1 ? "éléments actifs" : "élément actif"}
            </div>
          </div>
          <button
            data-testid="cart-drawer-close"
            onClick={onClose}
            aria-label="Fermer le panier"
            style={{
              background: "transparent", border: "none",
              fontSize: 24, lineHeight: 1, cursor: "pointer",
              padding: 6, color: "var(--civ-text, #0f172a)",
              opacity: 0.7,
            }}
          >×</button>
        </header>

        {/* Tabs */}
        <nav style={{
          display: "flex", gap: 4, padding: "10px 14px 4px",
          borderBottom: "1px solid var(--civ-border, rgba(0,0,0,0.06))",
        }}>
          {[
            { k: "all", label: "Tout", count: totalItems },
            { k: "items", label: "Panier", count: itemsCount },
            { k: "orders", label: "Livraisons", count: orders.length },
            { k: "bookings", label: "Réservations", count: bookings.length },
          ].map((t) => (
            <button
              key={t.k}
              data-testid={`cart-tab-${t.k}`}
              onClick={() => setTab(t.k)}
              style={{
                flex: 1,
                padding: "8px 10px",
                fontSize: 13,
                fontWeight: 700,
                background: tab === t.k ? "rgba(255,126,27,0.12)" : "transparent",
                color: tab === t.k ? "#FF7E1B" : "var(--civ-text, #334155)",
                border: "none",
                borderRadius: 10,
                cursor: "pointer",
                transition: "background 150ms",
              }}
            >{t.label} {t.count > 0 && <span style={{ opacity: 0.65 }}>({t.count})</span>}</button>
          ))}
        </nav>

        {/* Body */}
        <div style={{ flex: 1, overflowY: "auto", padding: "12px 14px", minHeight: 0 }}>
          {loading && (
            <div data-testid="cart-loading" style={{ textAlign: "center", padding: 32, opacity: 0.6, fontSize: 14 }}>
              Chargement…
            </div>
          )}
          {error && (
            <div data-testid="cart-error" style={{
              padding: 12, background: "rgba(239,68,68,0.08)",
              borderRadius: 10, color: "#DC2626", fontSize: 13,
            }}>{error}</div>
          )}
          {authNeeded && (
            <div data-testid="cart-auth-needed" style={{
              padding: 20, textAlign: "center",
              background: "rgba(255,126,27,0.06)",
              borderRadius: 12, margin: "20px 0",
            }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>🔒</div>
              <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>
                Connecte-toi pour voir ton panier
              </div>
              <div style={{ fontSize: 12.5, opacity: 0.75, marginBottom: 14 }}>
                Retrouve toutes tes livraisons et réservations en un seul endroit.
              </div>
              <button
                data-testid="cart-goto-login"
                onClick={() => { onClose?.(); window.location.href = "/auth"; }}
                style={{
                  padding: "9px 18px", borderRadius: 10, border: "none",
                  background: "#FF7E1B", color: "#fff", fontWeight: 700,
                  fontSize: 13, cursor: "pointer",
                }}
              >Se connecter</button>
            </div>
          )}
          {!loading && !authNeeded && !error && totalItems === 0 && (
            <div data-testid="cart-empty" style={{
              padding: "36px 12px", textAlign: "center", opacity: 0.75,
            }}>
              <div style={{ fontSize: 40, marginBottom: 8 }}>🌾</div>
              <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>
                Ton panier est vide pour le moment
              </div>
              <div style={{ fontSize: 12.5, opacity: 0.85, lineHeight: 1.5 }}>
                Ajoute un produit avec le bouton <strong>＋</strong> ou passe une commande depuis le chat.
                Elle apparaîtra ici en temps réel.
              </div>
            </div>
          )}

          {/* v3.0.0-rc.8e — Section "Panier" (items ajoutés via ➕ sur les cartes) */}
          {!loading && !authNeeded && showItems && localItems.length > 0 && (
            <section data-testid="cart-items-section" style={{ marginBottom: 14 }}>
              {tab === "all" && (
                <h3 style={{
                  fontSize: 11, fontWeight: 800, textTransform: "uppercase",
                  letterSpacing: 0.6, opacity: 0.55, margin: "6px 4px 8px",
                }}>Panier — {fmtFcfa(itemsTotal)}</h3>
              )}
              {tab === "items" && (
                <div style={{
                  padding: "8px 4px 10px",
                  fontSize: 13, opacity: 0.75,
                }}>
                  Total : <strong style={{ color: "#FF7E1B" }}>{fmtFcfa(itemsTotal)}</strong>
                </div>
              )}
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {localItems.map((it) => (
                  <LocalItemCard key={it.product_id} item={it} />
                ))}
              </div>
              {tab === "items" && localItems.length > 0 && (
                <button
                  data-testid="cart-clear"
                  onClick={() => {
                    if (window.confirm("Vider ton panier ?")) clearCart();
                  }}
                  style={{
                    marginTop: 10, width: "100%",
                    padding: "8px 12px", borderRadius: 8,
                    background: "transparent", color: "#EF4444",
                    border: "1px solid rgba(239,68,68,0.35)",
                    fontWeight: 700, fontSize: 12, cursor: "pointer",
                  }}
                >🗑️ Vider le panier</button>
              )}
            </section>
          )}

          {!loading && !authNeeded && showOrders && dispatchOrders.length > 0 && (
            <section data-testid="cart-dispatch-orders-section" style={{ marginBottom: 14 }}>
              {tab === "all" && (
                <h3 style={{
                  fontSize: 11, fontWeight: 800, textTransform: "uppercase",
                  letterSpacing: 0.6, opacity: 0.55, margin: "6px 4px 8px",
                }}>Suivi en direct</h3>
              )}
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {dispatchOrders.map((o) => (
                  <DispatchOrderCard key={o.order_id} order={o} onReopenInChat={reopenInChat} />
                ))}
              </div>
            </section>
          )}

          {!loading && !authNeeded && showOrders && orders.length > 0 && (
            <section data-testid="cart-orders-section" style={{ marginBottom: 14 }}>
              {tab === "all" && (
                <h3 style={{
                  fontSize: 11, fontWeight: 800, textTransform: "uppercase",
                  letterSpacing: 0.6, opacity: 0.55, margin: "6px 4px 8px",
                }}>Livraisons en cours</h3>
              )}
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {orders.map((o) => (
                  <OrderCard key={o.order_ref} order={o} />
                ))}
              </div>
            </section>
          )}

          {!loading && !authNeeded && showBookings && bookings.length > 0 && (
            <section data-testid="cart-bookings-section">
              {tab === "all" && (
                <h3 style={{
                  fontSize: 11, fontWeight: 800, textTransform: "uppercase",
                  letterSpacing: 0.6, opacity: 0.55, margin: "6px 4px 8px",
                }}>Réservations</h3>
              )}
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {bookings.map((b) => (
                  <BookingCard key={b.booking_id} booking={b} />
                ))}
              </div>
            </section>
          )}
        </div>

        {/* Footer */}
        <footer style={{
          padding: "12px 14px",
          borderTop: "1px solid var(--civ-border, rgba(0,0,0,0.08))",
          display: "flex", flexDirection: "column", gap: 8,
        }}>
          {localItems.length > 0 && (
            <CheckoutButton items={localItems} onSuccess={onClose} />
          )}
          <button
            data-testid="cart-goto-account"
            onClick={() => { onClose?.(); onGoToAccount?.(); }}
            style={{
              width: "100%", padding: "11px 14px", borderRadius: 12,
              background: "#0F172A", color: "#fff", border: "none",
              fontWeight: 700, fontSize: 13.5, cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
            }}
          >
            <span>Mon compte</span>
            <span aria-hidden style={{ fontSize: 16 }}>→</span>
          </button>
        </footer>

        <style>{`
          @keyframes cartFadeIn { from { opacity: 0 } to { opacity: 1 } }
          @keyframes cartSlideIn { from { transform: translateX(100%) } to { transform: translateX(0) } }
        `}</style>
      </aside>
    </div>
  );
}

function DispatchOrderCard({ order, onReopenInChat }) {
  // v3.0.0-rc.8k — Cart Tracking Sync : rend la MÊME carte de suivi live que
  // le chat (OrderTrackingLive → RX template `order_tracking`). Bouton
  // "Ouvrir dans le chat" pour ré-injecter cette carte dans la conversation.
  const userId = (typeof window !== "undefined" && localStorage.getItem("civ_user_phone")) || null;
  return (
    <article
      data-testid={`cart-dispatch-order-${order.order_id}`}
      style={{
        border: "1px solid var(--civ-border, rgba(0,0,0,0.08))",
        borderRadius: 14,
        padding: 10,
        background: "var(--civ-bg-elev, rgba(255,255,255,0.6))",
        display: "flex", flexDirection: "column", gap: 8,
      }}
    >
      <OrderTrackingLive orderId={order.order_id} userId={userId} />
      <button
        type="button"
        data-testid={`cart-dispatch-reopen-${order.order_id}`}
        onClick={() => onReopenInChat && onReopenInChat(order)}
        style={{
          alignSelf: "flex-end",
          background: "transparent",
          border: "1px solid rgba(255,126,27,0.55)",
          color: "#FF7E1B",
          borderRadius: 999,
          padding: "6px 12px",
          fontSize: 12, fontWeight: 700,
          cursor: "pointer",
        }}
      >💬 Ouvrir dans le chat</button>
    </article>
  );
}

function OrderCard({ order }) {
  const s = STATUS_LABEL[order.status] || { txt: order.status, color: "#64748B" };
  return (
    <article
      data-testid={`cart-order-${order.order_ref}`}
      style={{
        border: "1px solid var(--civ-border, rgba(0,0,0,0.08))",
        borderRadius: 12,
        padding: 12,
        background: "var(--civ-bg-elev, rgba(255,255,255,0.6))",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", gap: 8, marginBottom: 6 }}>
        <div style={{ fontWeight: 700, fontSize: 14, minWidth: 0, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {order.commerce_name}
        </div>
        <div style={{
          fontSize: 10, fontWeight: 800, padding: "3px 8px",
          borderRadius: 999, background: s.color + "22", color: s.color,
          textTransform: "uppercase", letterSpacing: 0.4, flexShrink: 0,
        }}>{s.txt}</div>
      </div>
      <div style={{ fontSize: 12.5, opacity: 0.75, marginBottom: 6 }}>
        {order.items_summary || "1 colis"}
      </div>
      {order.drop_description && (
        <div style={{ fontSize: 11.5, opacity: 0.6, marginBottom: 6 }}>
          📍 {order.drop_description}
        </div>
      )}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 12 }}>
        <span style={{ opacity: 0.55 }}>Ref {order.order_ref}</span>
        <a
          data-testid={`cart-order-track-${order.order_ref}`}
          href={`/track/${order.order_ref}`}
          style={{
            color: "#FF7E1B", fontWeight: 700, textDecoration: "none",
          }}
        >Suivre →</a>
      </div>
    </article>
  );
}

function BookingCard({ booking }) {
  const s = STATUS_LABEL[booking.status] || { txt: booking.status || "en attente", color: "#F59E0B" };
  return (
    <article
      data-testid={`cart-booking-${booking.booking_id}`}
      style={{
        border: "1px solid var(--civ-border, rgba(0,0,0,0.08))",
        borderRadius: 12,
        padding: 12,
        background: "var(--civ-bg-elev, rgba(255,255,255,0.6))",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", gap: 8, marginBottom: 6 }}>
        <div style={{ fontWeight: 700, fontSize: 14, minWidth: 0, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {booking.profile_name || "Consultation"}
        </div>
        <div style={{
          fontSize: 10, fontWeight: 800, padding: "3px 8px",
          borderRadius: 999, background: s.color + "22", color: s.color,
          textTransform: "uppercase", letterSpacing: 0.4, flexShrink: 0,
        }}>{s.txt}</div>
      </div>
      <div style={{ fontSize: 12.5, opacity: 0.75, marginBottom: 6 }}>
        📅 {booking.date} à {booking.time}
      </div>
      {booking.reason && (
        <div style={{ fontSize: 11.5, opacity: 0.6, marginBottom: 6 }}>
          {booking.reason}
        </div>
      )}
      <div style={{ fontSize: 11, opacity: 0.55 }}>
        {fmtDate(booking.created_at)}
      </div>
    </article>
  );
}

// v3.0.0-rc.8e — Item du panier local (ajouté via ➕ sur les cartes produits).
function LocalItemCard({ item }) {
  const total = (item.qty || 0) * (item.price_fcfa || 0);
  return (
    <article
      data-testid={`cart-local-item-${item.product_id}`}
      style={{
        border: "1px solid var(--civ-border, rgba(0,0,0,0.08))",
        borderRadius: 12,
        padding: 10,
        background: "var(--civ-bg-elev, rgba(255,255,255,0.6))",
        display: "flex", gap: 10, alignItems: "center",
      }}
    >
      <div style={{
        width: 48, height: 48, borderRadius: 8, flexShrink: 0,
        background: "rgba(232,213,163,0.10)",
        display: "flex", alignItems: "center", justifyContent: "center",
        overflow: "hidden", fontSize: 22,
      }}>
        {item.photo
          ? <img src={item.photo} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} onError={(e) => { e.target.style.display = "none"; }} />
          : (item.emoji || "🛍")}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 700, fontSize: 13, lineHeight: 1.2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {item.name}
        </div>
        <div style={{ fontSize: 11, opacity: 0.6, marginTop: 2 }}>
          {item.merchant_name || "—"}
        </div>
        <div style={{ fontSize: 12, color: "#FF7E1B", fontWeight: 800, marginTop: 4, fontFeatureSettings: "'tnum'" }}>
          {fmtFcfa(total)}
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <button
          data-testid={`cart-item-minus-${item.product_id}`}
          onClick={() => setQty(item.product_id, (item.qty || 0) - 1)}
          aria-label="Diminuer la quantité"
          style={{
            width: 26, height: 26, borderRadius: 8,
            background: "transparent",
            border: "1px solid var(--civ-border, rgba(0,0,0,0.15))",
            color: "var(--civ-text, #334155)",
            cursor: "pointer", fontSize: 14, fontWeight: 800, lineHeight: 1,
          }}
        >−</button>
        <span
          data-testid={`cart-item-qty-${item.product_id}`}
          style={{ minWidth: 20, textAlign: "center", fontWeight: 800, fontSize: 13 }}
        >{item.qty}</span>
        <button
          data-testid={`cart-item-plus-${item.product_id}`}
          onClick={() => setQty(item.product_id, (item.qty || 0) + 1)}
          aria-label="Augmenter la quantité"
          style={{
            width: 26, height: 26, borderRadius: 8,
            background: "#FF7E1B", color: "#fff", border: "none",
            cursor: "pointer", fontSize: 14, fontWeight: 800, lineHeight: 1,
          }}
        >+</button>
      </div>
    </article>
  );
}

// v3.17.1 — Panier Famille : génère le lien partageable pour qu'un proche
// (diaspora) paie ce panier à distance (carte mock ou wallet AYA).
function FamilyBasketButton({ items }) {
  const [state, setState] = useState("idle"); // idle | loading | done | error
  const [result, setResult] = useState(null);
  const create = async () => {
    const token = localStorage.getItem("civ_user_token");
    if (!token) { setState("error"); setResult({ error: "Connecte-toi d'abord pour créer ton lien Panier Famille." }); return; }
    setState("loading");
    try {
      const API = process.env.REACT_APP_BACKEND_URL || "";
      const r = await fetch(`${API}/api/v3/family-basket/create`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          items: items.map((it) => ({ merchant_id: it.merchant_id, product_id: it.product_id, name: it.name, price_fcfa: it.price_fcfa, qty: it.qty })),
        }),
      });
      const d = await r.json();
      if (!r.ok || !d.ok) throw new Error(d.error || "création impossible");
      setResult(d);
      setState("done");
    } catch (e) {
      setResult({ error: e?.message || "erreur" });
      setState("error");
    }
  };
  if (state === "done" && result) {
    return <FamilyBasketShareCard data={{ basket_id: result.basket_id, share_path: result.share_path, total_fcfa: result.total_fcfa }} />;
  }
  return (
    <div>
      <button
        data-testid="cart-family-basket-btn"
        onClick={create}
        disabled={state === "loading"}
        style={{
          width: "100%", padding: "11px 14px", borderRadius: 12,
          background: "linear-gradient(135deg, #EFC75E, #D9A83C)", color: "#1A1206",
          border: "none", fontWeight: 800, fontSize: 13.5, cursor: "pointer",
          opacity: state === "loading" ? 0.6 : 1,
        }}
      >{state === "loading" ? "⏳ Création du lien…" : "💛 Faire payer un proche (Panier Famille)"}</button>
      {state === "error" && (
        <div data-testid="cart-family-basket-error" style={{ marginTop: 6, fontSize: 12, color: "#EF4444" }}>{result?.error}</div>
      )}
    </div>
  );
}

// v3.0.0-rc.8f — "Tout commander" : envoie le panier local à /api/v3/cart/checkout.
// Regroupe automatiquement par merchant côté backend. Feedback inline en 3 états :
//   idle → loading → success/error. Sur success : purge panier local + toast.
function CheckoutButton({ items, onSuccess }) {
  const [state, setState] = useState("idle"); // idle | loading | done | error | need_address | choose_address | choose_payment | mobile_money
  const [result, setResult] = useState(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [addresses, setAddresses] = useState([]);
  const [selectedCiv, setSelectedCiv] = useState(null);
  const [civForPay, setCivForPay] = useState(null);

  const total = items.reduce((s, x) => s + (x.qty || 0) * (x.price_fcfa || 0), 0);
  const merchantsCount = new Set(items.map((x) => x.merchant_id)).size;

  // v3.0.0-rc.8g/8h — Récupère la liste (0/1/N) et pilote le gate.
  // 2026-06 — Fusionne aussi les Domiciles créés via le modal géoloc (POST /v3/addresses),
  // qui n'apparaissent pas dans my-claims : une adresse créée = une adresse livrable.
  const fetchAddresses = useCallback(async () => {
    const token = localStorage.getItem("civ_user_token") || "";
    if (!token) return [];
    const out = [];
    try {
      const r = await fetch(`${API}/v3/addresses/my-claims`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.ok) {
        const b = await r.json();
        const list = b.claims || b.addresses || b.items || [];
        if (Array.isArray(list)) out.push(...list);
      }
    } catch (_) { /* non-fatal */ }
    try {
      const uid = localStorage.getItem("jsonfirst_civ_userid") || "";
      if (uid) {
        const r2 = await fetch(`${API}/v3/addresses?user_id=${encodeURIComponent(uid)}`);
        if (r2.ok) {
          const b2 = await r2.json();
          const extras = [b2.home, ...(b2.others || [])].filter(Boolean);
          for (const a of extras) {
            if (a.address_civ && !out.some((x) => x.address_civ === a.address_civ)) {
              out.push({
                address_civ: a.address_civ,
                friendly_name: a.name || "Domicile",
                commune_name: a.commune || a.commune_name || null,
              });
            }
          }
        }
      }
    } catch (_) { /* non-fatal */ }
    return out;
  }, []);

  // Succès partagé wallet + mobile money : purge panier + event chat + état done.
  const finishSuccess = useCallback((b, civ) => {
    setState("done");
    setResult(b);
    clearCart();
    try {
      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent("aya:cart-checkout-success", {
          detail: {
            orders: b.orders || [],
            errors: b.errors || [],
            delivery_address_civ: b.delivery_address_civ || civ || null,
            total_fcfa: (b.orders || []).reduce((s, o) => s + (o.amount_fcfa || 0), 0),
            parent_entry_id: b.parent_entry_id || null,
            new_balance_fcfa: b.new_balance_fcfa,
          },
        }));
      }
    } catch (_) { /* non-fatal */ }
    setTimeout(() => { onSuccess && onSuccess(); }, 2500);
  }, [onSuccess]);

  const doCheckout = useCallback(async (civ) => {
    setState("loading");
    setResult(null);
    const token = localStorage.getItem("civ_user_token") || "";
    try {
      const idKey = `chk_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      const r = await fetch(`${API}/v3/cart/checkout`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
          "Idempotency-Key": idKey,
        },
        body: JSON.stringify({ items, delivery_address_civ: civ || null }),
      });
      const b = await r.json();
      if (!r.ok || !b.ok) {
        setState("error");
        setResult({ error: b.error || "checkout_failed", errors: b.errors });
        return;
      }
      finishSuccess(b, civ);
    } catch (e) {
      setState("error");
      setResult({ error: "Erreur réseau" });
    }
  }, [items, finishSuccess]);

  // Étape intermédiaire : adresse choisie → choix du moyen de paiement.
  const proceedToPayment = useCallback((civ) => {
    setCivForPay(civ || null);
    setState("choose_payment");
  }, []);

  const handleCheckout = useCallback(async () => {
    if (state === "loading") return;
    const token = localStorage.getItem("civ_user_token") || "";
    if (!token) { setState("error"); setResult({ error: "Connexion requise" }); return; }
    const list = await fetchAddresses();
    if (list.length === 0) {
      setState("need_address");
      setWizardOpen(true);
      return;
    }
    if (list.length >= 2) {
      // v3.0.0-rc.8h — Multi-adresses : choix explicite avant paiement.
      setAddresses(list);
      setSelectedCiv(list[0]?.address_civ || null);
      setState("choose_address");
      return;
    }
    // Une seule adresse → choix du moyen de paiement.
    proceedToPayment(list[0]?.address_civ);
  }, [state, fetchAddresses, proceedToPayment]);

  if (state === "done") {
    return (
      <div
        data-testid="cart-checkout-success"
        style={{
          padding: "10px 14px", borderRadius: 12,
          background: "rgba(16,185,129,0.12)",
          border: "1px solid #10B981",
          color: "#065F46", fontSize: 13, textAlign: "center",
          fontWeight: 700,
        }}
      >✓ {result?.orders?.length || 0} commande{(result?.orders?.length || 0) > 1 ? "s" : ""} créée{(result?.orders?.length || 0) > 1 ? "s" : ""}</div>
    );
  }
  if (state === "choose_address") {
    return (
      <div data-testid="cart-checkout-choose-address" style={{
        padding: 12, borderRadius: 12,
        background: "linear-gradient(135deg, rgba(59,130,246,0.10), rgba(99,102,241,0.06))",
        border: "1px solid rgba(147,197,253,0.35)",
        display: "flex", flexDirection: "column", gap: 10,
      }}>
        <div style={{
          fontSize: 11, fontWeight: 800, letterSpacing: 0.6,
          textTransform: "uppercase", color: "#93C5FD",
          display: "flex", alignItems: "center", gap: 6,
        }}>
          <span aria-hidden>📍</span>
          <span>Où livrer ?</span>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {addresses.map((a) => (
            <label
              key={a.address_civ}
              data-testid={`checkout-addr-${a.address_civ}`}
              style={{
                display: "flex", gap: 10, alignItems: "flex-start",
                padding: 10, borderRadius: 10,
                border: `1px solid ${selectedCiv === a.address_civ ? "#3B82F6" : "rgba(147,197,253,0.25)"}`,
                background: selectedCiv === a.address_civ ? "rgba(59,130,246,0.14)" : "transparent",
                cursor: "pointer",
              }}
            >
              <input
                type="radio"
                name="checkout-address"
                value={a.address_civ}
                checked={selectedCiv === a.address_civ}
                onChange={() => setSelectedCiv(a.address_civ)}
                style={{ marginTop: 4 }}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 13.5, color: "var(--civ-text, #0F172A)" }}>
                  {a.friendly_name} {a.is_business && <span style={{ fontSize: 10, color: "#6366F1" }}>· Pro</span>}
                </div>
                <div style={{ fontSize: 11.5, opacity: 0.7, marginTop: 2 }}>
                  Commune de <strong>{a.commune_name}</strong>{a.quartier ? `, ${a.quartier}` : ""}
                  {a.street && <> — {a.street}{a.house_number ? ` n°${a.house_number}` : ""}</>}
                </div>
                <code style={{
                  fontFamily: "ui-monospace, Menlo, monospace",
                  fontSize: 10, opacity: 0.55, marginTop: 3, display: "inline-block",
                }}>{a.address_civ}</code>
              </div>
            </label>
          ))}
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          <button
            data-testid="checkout-addr-cancel"
            onClick={() => setState("idle")}
            style={{
              flex: 1, padding: "9px 12px", borderRadius: 10,
              background: "transparent", color: "var(--civ-text, #334155)",
              border: "1px solid var(--civ-border, rgba(0,0,0,0.15))",
              fontWeight: 600, fontSize: 12.5, cursor: "pointer",
            }}
          >Annuler</button>
          <button
            data-testid="checkout-addr-confirm"
            onClick={() => proceedToPayment(selectedCiv)}
            disabled={!selectedCiv}
            style={{
              flex: 2, padding: "9px 12px", borderRadius: 10,
              background: "linear-gradient(135deg, #FF7E1B, #F59E0B)",
              color: "#fff", border: "none",
              fontWeight: 800, fontSize: 13, cursor: "pointer",
              opacity: selectedCiv ? 1 : 0.5,
            }}
          >Livrer ici ✓</button>
        </div>
      </div>
    );
  }
  if (state === "choose_payment") {
    const totalPay = items.reduce((s, x) => s + (x.qty || 0) * (x.price_fcfa || 0), 0);
    return (
      <div data-testid="cart-choose-payment" style={{
        padding: 12, borderRadius: 12,
        background: "linear-gradient(135deg, rgba(255,126,27,0.08), rgba(245,158,11,0.05))",
        border: "1px solid rgba(251,191,36,0.35)",
        display: "flex", flexDirection: "column", gap: 8,
      }}>
        <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.6, textTransform: "uppercase", color: "#B45309" }}>
          💳 Comment payer {fmtFcfa(totalPay)} ?
        </div>
        <button
          data-testid="pay-with-wallet-btn"
          onClick={() => doCheckout(civForPay)}
          style={{
            padding: "11px 12px", borderRadius: 10,
            background: "linear-gradient(135deg, #FF7E1B, #F59E0B)", color: "#fff",
            border: "none", fontWeight: 800, fontSize: 13.5, cursor: "pointer", textAlign: "left",
          }}
        >💼 Wallet AYA <span style={{ fontWeight: 500, fontSize: 11.5, opacity: 0.9 }}>— solde débité instantanément</span></button>
        <button
          data-testid="pay-with-mobile-money-btn"
          onClick={() => setState("mobile_money")}
          style={{
            padding: "11px 12px", borderRadius: 10,
            background: "linear-gradient(135deg, #10B981, #059669)", color: "#fff",
            border: "none", fontWeight: 800, fontSize: 13.5, cursor: "pointer", textAlign: "left",
          }}
        >📱 Mobile Money <span style={{ fontWeight: 500, fontSize: 11.5, opacity: 0.9 }}>— Orange · Wave · MTN (sandbox)</span></button>
        <button
          data-testid="pay-choice-cancel"
          onClick={() => setState("idle")}
          style={{
            padding: "8px 10px", borderRadius: 8, background: "transparent",
            color: "var(--civ-text, #334155)", border: "1px solid var(--civ-border, rgba(0,0,0,0.15))",
            fontWeight: 600, fontSize: 12, cursor: "pointer",
          }}
        >Annuler</button>
      </div>
    );
  }
  if (state === "mobile_money") {
    const mmTotal = items.reduce((s, x) => s + (x.qty || 0) * (x.price_fcfa || 0), 0);
    return (
      <MobileMoneyCheckout
        amountFcfa={mmTotal}
        purpose="cart_checkout"
        metadata={{ items, delivery_address_civ: civForPay || null }}
        description={`Panier AYA — ${items.length} article(s)`}
        onCancel={() => setState("choose_payment")}
        onConfirmed={(body) => finishSuccess(body || {}, civForPay)}
      />
    );
  }
  return (
    <>
      {state === "error" && (
        <div
          data-testid="cart-checkout-error"
          style={{
            padding: "10px 14px", borderRadius: 10,
            background: "rgba(239,68,68,0.08)", border: "1px solid #EF4444",
            color: "#B91C1C", fontSize: 12.5, marginBottom: 8,
          }}
        >
          <div style={{ fontWeight: 700, marginBottom: 4 }}>⚠️ Impossible de finaliser</div>
          <div>{result?.error || "erreur inconnue"}</div>
          <button
            onClick={() => setState("idle")}
            style={{
              marginTop: 6, padding: "4px 10px", borderRadius: 6,
              background: "#fff", color: "#B91C1C",
              border: "1px solid #EF4444", cursor: "pointer",
              fontSize: 11, fontWeight: 700,
            }}
          >Réessayer</button>
        </div>
      )}
      {state === "need_address" && (
        <div
          data-testid="cart-checkout-need-address"
          style={{
            padding: "10px 14px", borderRadius: 10,
            background: "rgba(255,126,27,0.10)", border: "1px solid #FF7E1B",
            color: "#9A3412", fontSize: 12.5, marginBottom: 8,
          }}
        >📍 <strong>Il faut une adresse de livraison</strong> avant de commander. Ouverture du créateur d&apos;adresse…</div>
      )}
      <button
        data-testid="cart-checkout-btn"
        onClick={handleCheckout}
        disabled={state === "loading" || items.length === 0}
        style={{
          width: "100%",
          padding: "12px 14px", borderRadius: 12,
          background: "linear-gradient(135deg, #FF7E1B, #F59E0B)",
          color: "#fff", border: "none",
          fontWeight: 800, fontSize: 14, cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          boxShadow: "0 4px 12px rgba(255,126,27,0.35)",
          opacity: state === "loading" ? 0.7 : 1,
        }}
      >
        <span>{state === "loading" ? "⏳ Traitement…" : `🛒 Tout commander (${merchantsCount})`}</span>
        <span style={{ fontFeatureSettings: "'tnum'", fontSize: 13.5 }}>
          {fmtFcfa(total)}
        </span>
      </button>
      <ClaimAddressWizard
        open={wizardOpen}
        onClose={() => { setWizardOpen(false); if (state === "need_address") setState("idle"); }}
        onSuccess={() => {
          setWizardOpen(false);
          setState("idle");
          // Après création d'adresse, relance auto le checkout.
          setTimeout(() => handleCheckout(), 250);
        }}
      />
    </>
  );
}
